import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { useTranslations } from '../hooks/useTranslations';
import { translateCategories, translateCategory, translateCountry, languageNames, availableLanguages } from '../utils/translations';
import { getSensitiveTopicLabel, getSensitiveUILabel } from '../utils/sensitiveTopicsTranslations';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { HeroSection } from '../components/home/HeroSection';
import { ProductCard } from '../components/catalog/ProductCard';
import { ProductTable } from '../components/catalog/ProductTable';
import { ProductDetailDialog } from '../components/catalog/ProductDetailDialog';
import { PaginationControls } from '../components/catalog/PaginationControls';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger,
  SheetHeader,
  SheetTitle 
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Filter, 
  LayoutGrid, 
  List, 
  SlidersHorizontal, 
  Loader2,
  Search,
  X,
  Globe,
  FolderOpen,
  RotateCcw,
  TrendingUp,
  AlertTriangle,
  Download,
  ChevronDown,
  Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const HomePage = () => {
  const { language } = useLanguage();
  const { token } = useAuth();
  const { trackPageView, trackProductView, trackSearch, trackFilter, trackDownload, trackClick } = useAnalytics();
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // Changed default to table
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Sort state for server-side sorting
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filter options (loaded from DB)
  const [uniqueCountries, setUniqueCountries] = useState([]);
  const [uniqueCategories, setUniqueCategories] = useState([]);
  const [sensitiveCategories, setSensitiveCategories] = useState([]);

  // Active filters - countries and categories are now arrays for multi-select
  // Sensitive topics are now dynamic based on backend categories
  const [filters, setFilters] = useState({
    search: '',
    countries: [],
    categories: [],
    sensitiveTopics: [], // Dynamic array of selected sensitive topic keys
    dr_min: '',
    dr_max: '',
    traffic_min: '',
    traffic_max: '',
    price_min: '',
    price_max: '',
    siteTypes: [],
  });

  // Dropdowns open state
  const [countriesOpen, setCountriesOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [siteTypesOpen, setSiteTypesOpen] = useState(false);
  const [sensitiveTopicsOpen, setSensitiveTopicsOpen] = useState(false);
  
  // Search states for dropdowns
  const [countrySearch, setCountrySearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [sensitiveTopicSearch, setSensitiveTopicSearch] = useState('');

  // Available site types
  const siteTypeOptions = [
    { value: 'Periódicos', label: language === 'es' ? 'Periódicos' : 'Newspapers' },
    { value: 'Web/Blog', label: 'Web/Blog' },
  ];

  // Dynamic sensitive topic options from backend
  // Transform sensitiveCategories into options format for dropdown with translations
  const sensitiveTopicOptions = sensitiveCategories.map(cat => ({
    key: cat.key || cat.name.toLowerCase().replace(/\s+/g, '_'),
    label: getSensitiveTopicLabel(cat.key || cat.name, language),
    id: cat.id
  }));

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown="countries"]') && 
          !event.target.closest('[data-dropdown="categories"]') &&
          !event.target.closest('[data-dropdown="siteTypes"]') &&
          !event.target.closest('[data-dropdown="sensitiveTopics"]')) {
        setCountriesOpen(false);
        setCategoriesOpen(false);
        setSiteTypesOpen(false);
        setSensitiveTopicsOpen(false);
        setCountrySearch('');
        setCategorySearch('');
        setSensitiveTopicSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Use centralized translations
  const translations = useTranslations();
  
  // Build texts object from centralized translations for backwards compatibility
  const t = {
    catalog: translations.catalog.title,
    results: translations.catalog.results,
    search: translations.catalog.searchPlaceholder,
    searchLabel: translations.common.search,
    country: translations.products.country,
    allCountries: translations.products.allCountries,
    category: translations.products.category,
    allCategories: translations.products.allCategories,
    sensitiveTopics: translations.catalog.sensitiveTopics,
    searchCountry: translations.catalog.searchCountry,
    searchCategory: translations.catalog.searchCategory,
    resetFilters: translations.catalog.resetFilters,
    casino: translations.catalog.casino,
    cbd: translations.catalog.cbd,
    gambling: translations.catalog.gambling,
    cryptocurrency: translations.catalog.cryptocurrency,
    sex: translations.catalog.sex,
    erotic: translations.catalog.erotic,
    vpn: translations.catalog.vpn,
    tarot: translations.catalog.tarot,
    porn: translations.catalog.porn,
    cannabis: translations.catalog.cannabis,
    filters: translations.catalog.filters,
    clearFilters: translations.products.clearFilters,
    applyFilters: translations.catalog.applyFilters,
    noResults: translations.catalog.noResults,
    tryOther: translations.catalog.tryOther,
    page: translations.common.page,
    of: translations.common.of,
    showing: translations.common.showing,
    activeFilters: translations.catalog.activeFilters,
    dr: translations.catalog.drLabel,
    drMin: translations.products.drMin,
    drMax: translations.products.drMax,
    traffic: translations.catalog.trafficLabel,
    trafficMin: translations.products.trafficMin,
    trafficMax: translations.products.trafficMax,
    tableUrl: translations.catalog.tableUrl,
    tableLanguage: translations.catalog.tableLanguage,
    tableCountry: translations.products.country,
    tableSiteType: translations.catalog.tableSiteType,
    tableCategory: translations.products.category,
    tableLinkType: translations.catalog.tableLinkType,
    tableTag: translations.catalog.tableTag,
    tableRd: translations.catalog.tableRd,
    tableTraffic: translations.catalog.trafficLabel,
    tablePrice: translations.products.price,
    tableDr: translations.products.dr,
    tablePriceSensitive: translations.catalog.tablePriceSensitive,
    tableMoreInfo: translations.catalog.tableMoreInfo,
    tableSimilarSites: translations.catalog.tableSimilarSites,
    moreInfoTitle: translations.catalog.moreInfoTitle,
    linkDuration: translations.catalog.linkDuration,
    noSensitivePrice: translations.catalog.noSensitivePrice,
    backToStart: translations.catalog.backToStart,
    filtersActive: translations.catalog.filtersActive,
    itemsPerPage: translations.catalog.itemsPerPage,
    showingItems: translations.catalog.showingItems,
    countries: translations.catalog.countries,
    categories: translations.catalog.categories,
    rangeInfo: translations.catalog.rangeInfo,
    monthlyVisits: translations.catalog.monthlyVisits,
    minPrice: translations.catalog.minPrice,
    maxPrice: translations.catalog.maxPrice,
    priceRange: translations.catalog.priceRange,
    price: translations.products.price,
    cookieTitle: translations.cookies.title,
    cookieMessage: translations.cookies.message,
    cookieMoreInfo: translations.cookies.moreInfo,
    cookieAccept: translations.cookies.accept,
    cookieReject: translations.cookies.reject,
  };

  // Fetch filter options on mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Track page view and fetch products on mount
  useEffect(() => {
    trackPageView('/catalog');
    fetchProducts(1);
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/filters/options`);
      setUniqueCountries(response.data.countries || []);
      setUniqueCategories(response.data.categories || []);
      // Load sensitive categories from backend
      setSensitiveCategories(response.data.sensitive_categories || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchProducts = async (page = 1, customFilters = null, customLimit = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', customLimit || itemsPerPage);
      
      // Use custom filters if provided, otherwise use state filters
      const f = customFilters || filters;
      
      if (f.search) params.append('search', f.search);
      // Handle multiple countries
      if (f.countries && f.countries.length > 0) {
        f.countries.forEach(c => params.append('country', c));
      }
      // Handle multiple categories
      if (f.categories && f.categories.length > 0) {
        f.categories.forEach(c => params.append('category', c));
      }
      // Handle dynamic sensitive topics - send each topic as its own parameter (casino=true, cbd=true, etc.)
      if (f.sensitiveTopics && f.sensitiveTopics.length > 0) {
        f.sensitiveTopics.forEach(topic => {
          params.append(topic, 'true');
        });
      }
      if (f.dr_min) params.append('dr_min', f.dr_min);
      if (f.dr_max) params.append('dr_max', f.dr_max);
      if (f.traffic_min) params.append('traffic_min', f.traffic_min);
      if (f.traffic_max) params.append('traffic_max', f.traffic_max);
      if (f.price_min) params.append('price_min', f.price_min);
      if (f.price_max) params.append('price_max', f.price_max);
      // Handle multiple site types
      if (f.siteTypes && f.siteTypes.length > 0) {
        f.siteTypes.forEach(st => params.append('site_type', st));
      }
      
      // Add sorting parameters
      if (sortField) {
        params.append('sort_by', sortField);
        params.append('sort_order', sortOrder);
      }

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data.products);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle sort change from ProductTable
  const handleSortChange = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    // Fetch products with new sort, keeping page 1
    fetchProducts(1, filters, itemsPerPage);
  };

  // Effect to refetch when sort changes
  useEffect(() => {
    if (sortField) {
      fetchProducts(pagination.page, filters, itemsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder]);

  const handleApplyFilters = () => {
    fetchProducts(1);
    setMobileFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setSortField('');
    setSortOrder('desc');
    setFilters({
      search: '',
      countries: [],
      categories: [],
      sensitiveTopics: [],
      dr_min: '',
      dr_max: '',
      traffic_min: '',
      traffic_max: '',
      price_min: '',
      price_max: '',
      siteTypes: [],
    });
    // Fetch with cleared filters
    setTimeout(() => fetchProducts(1), 0);
  };

  const handlePageChange = (newPage) => {
    fetchProducts(newPage);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    fetchProducts(1, null, newLimit);
  };

  // Clean website URL for search
  const cleanSearchUrl = (url) => {
    if (!url) return '';
    return url
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '')
      .toLowerCase()
      .trim();
  };

  const handleFilterChange = (key, value) => {
    // Auto-clean URL when searching
    const cleanedValue = key === 'search' ? cleanSearchUrl(value) : value;
    const sensitiveTopics = ['casino', 'cbd', 'gambling', 'cryptocurrency', 'sex', 'erotic', 'vpn', 'tarot', 'porn', 'cannabis'];
    
    setFilters(prev => {
      const newFilters = { ...prev, [key]: cleanedValue };
      
      // Auto-fetch for sensitive topics and some other fields
      if (sensitiveTopics.includes(key) || ['dr_min', 'dr_max', 'traffic_min', 'traffic_max', 'price_min', 'price_max'].includes(key)) {
        setTimeout(() => fetchProducts(1, newFilters), 0);
      }
      
      return newFilters;
    });
  };

  // Auto-search with debounce for search field
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== undefined) {
        fetchProducts(1, filters);
        // Track search if query is not empty
        if (filters.search && filters.search.trim().length > 0) {
          trackSearch(filters.search.trim());
        }
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Toggle country selection
  const toggleCountry = (country) => {
    setFilters(prev => {
      const newCountries = prev.countries.includes(country)
        ? prev.countries.filter(c => c !== country)
        : [...prev.countries, country];
      const newFilters = { ...prev, countries: newCountries };
      // Track filter usage
      trackFilter('country', country, newFilters);
      // Auto-fetch with new filters
      setTimeout(() => fetchProducts(1, newFilters), 0);
      return newFilters;
    });
  };

  // Toggle category selection
  const toggleCategory = (category) => {
    setFilters(prev => {
      const newCategories = prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category];
      const newFilters = { ...prev, categories: newCategories };
      // Track filter usage
      trackFilter('category', category, newFilters);
      // Auto-fetch with new filters
      setTimeout(() => fetchProducts(1, newFilters), 0);
      return newFilters;
    });
  };

  // Open product detail dialog
  const openProductDialog = (product) => {
    setSelectedProduct(product);
    setProductDialogOpen(true);
    // Track product view with full details
    trackProductView(product);
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters.search !== '' ||
      (filters.countries && filters.countries.length > 0) ||
      (filters.categories && filters.categories.length > 0) ||
      (filters.sensitiveTopics && filters.sensitiveTopics.length > 0) ||
      filters.dr_min !== '' || filters.dr_max !== '' ||
      filters.traffic_min !== '' || filters.traffic_max !== '';
  };

  // Count active filters
  const activeFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.countries && filters.countries.length > 0) count += filters.countries.length;
    if (filters.categories && filters.categories.length > 0) count += filters.categories.length;
    if (filters.sensitiveTopics && filters.sensitiveTopics.length > 0) count += filters.sensitiveTopics.length;
    if (filters.dr_min || filters.dr_max) count++;
    if (filters.traffic_min || filters.traffic_max) count++;
    return count;
  };

  // Clear all filters and go back to start
  const clearAllFilters = () => {
    const emptyFilters = {
      search: '',
      countries: [],
      categories: [],
      sensitiveTopics: [],
      dr_min: '',
      dr_max: '',
      traffic_min: '',
      traffic_max: '',
      price_min: '',
      price_max: '',
      siteTypes: [],
    };
    setFilters(emptyFilters);
    fetchProducts(1, emptyFilters);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Download filtered results as Excel
  const [downloading, setDownloading] = useState(false);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  
  const handleDownloadFiltered = async () => {
    // Check if user is authenticated
    if (!token) {
      alert(language === 'es' 
        ? 'Debes iniciar sesión para descargar el catálogo' 
        : 'You must login to download the catalog');
      setShowDownloadConfirm(false);
      return;
    }
    
    setShowDownloadConfirm(false);
    setDownloading(true);
    
    // Track download attempt
    trackDownload('catalog_filtered', {
      format: 'xlsx',
      count: pagination.total,
      filters: {
        search: filters.search,
        countries: filters.countries,
        categories: filters.categories,
        dr_range: filters.dr_min || filters.dr_max ? `${filters.dr_min || 0}-${filters.dr_max || 100}` : null,
        traffic_range: filters.traffic_min || filters.traffic_max ? `${filters.traffic_min || 0}-${filters.traffic_max || '∞'}` : null,
        sensitive_topics: filters.sensitiveTopics || []
      }
    });
    
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      // Handle multiple countries
      if (filters.countries && filters.countries.length > 0) {
        filters.countries.forEach(c => params.append('country', c));
      }
      // Handle multiple categories
      if (filters.categories && filters.categories.length > 0) {
        filters.categories.forEach(c => params.append('category', c));
      }
      if (filters.dr_min) params.append('dr_min', filters.dr_min);
      if (filters.dr_max) params.append('dr_max', filters.dr_max);
      if (filters.traffic_min) params.append('traffic_min', filters.traffic_min);
      if (filters.traffic_max) params.append('traffic_max', filters.traffic_max);
      
      // Dynamic sensitive topics
      if (filters.sensitiveTopics && filters.sensitiveTopics.length > 0) {
        filters.sensitiveTopics.forEach(topic => params.append('sensitive_topic', topic));
      }

      const response = await axios.get(`${API}/products/export?${params.toString()}`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `catalogo_filtrado_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading:', error);
      alert(language === 'es' ? 'Error al descargar. Intenta de nuevo.' : 'Download error. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Find similar sites based on product attributes
  const findSimilarSites = (product) => {
    const drRange = 15; // +/- 15 DR
    
    // Reset filters first, then apply similar site criteria
    // IMPORTANT: Category must match for relevant results
    const newFilters = {
      search: '',
      countries: product.country ? [product.country] : [],
      categories: product.category ? [product.category] : [],  // Include category for better matching
      sensitiveTopics: [],
      dr_min: Math.max(0, (product.dr || 0) - drRange).toString(),
      dr_max: ((product.dr || 0) + drRange).toString(),
      traffic_min: '',
      traffic_max: '',
      price_min: '',
      price_max: '',
      siteTypes: [],
    };
    
    setFilters(newFilters);
    setProductDialogOpen(false);
    
    // Fetch with new filters
    setTimeout(() => {
      fetchProducts(1, newFilters);
    }, 100);
  };

  // Debounce timer ref at parent level
  const filterDebounceRef = useRef(null);
  
  // Debounced filter update function
  const updateFiltersDebounced = useCallback((newFilters) => {
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }
    filterDebounceRef.current = setTimeout(() => {
      setFilters(newFilters);
      fetchProducts(1, newFilters);
    }, 500);
  }, []);

  // Filter sidebar content component
  const FilterContent = ({ onApply }) => {
    // Local search states for filters - completely independent
    const [localCountrySearch, setLocalCountrySearch] = useState('');
    const [localCategorySearch, setLocalCategorySearch] = useState('');
    const [localSensitiveSearch, setLocalSensitiveSearch] = useState('');
    
    // Local state for text/number inputs to prevent re-render issues
    const [localSearch, setLocalSearch] = useState(filters.search);
    const [localDrMin, setLocalDrMin] = useState(filters.dr_min);
    const [localDrMax, setLocalDrMax] = useState(filters.dr_max);
    const [localTrafficMin, setLocalTrafficMin] = useState(filters.traffic_min);
    const [localTrafficMax, setLocalTrafficMax] = useState(filters.traffic_max);
    const [localPriceMin, setLocalPriceMin] = useState(filters.price_min || '');
    const [localPriceMax, setLocalPriceMax] = useState(filters.price_max || '');
    
    // Local state for sensitive topics (array of selected keys)
    const [localSensitiveTopics, setLocalSensitiveTopics] = useState(filters.sensitiveTopics || []);
    
    // Expanded accordion sections
    const [expandedSections, setExpandedSections] = useState(['search', 'country', 'category']);
    
    // Handle text input changes with debounce
    const handleSearchChange = (value) => {
      setLocalSearch(value);
      updateFiltersDebounced({ ...filters, search: value });
    };
    
    const handleDrMinChange = (value) => {
      setLocalDrMin(value);
      updateFiltersDebounced({ ...filters, dr_min: value });
    };
    
    const handleDrMaxChange = (value) => {
      setLocalDrMax(value);
      updateFiltersDebounced({ ...filters, dr_max: value });
    };
    
    const handleTrafficMinChange = (value) => {
      setLocalTrafficMin(value);
      updateFiltersDebounced({ ...filters, traffic_min: value });
    };
    
    const handleTrafficMaxChange = (value) => {
      setLocalTrafficMax(value);
      updateFiltersDebounced({ ...filters, traffic_max: value });
    };
    
    const handlePriceMinChange = (value) => {
      setLocalPriceMin(value);
      updateFiltersDebounced({ ...filters, price_min: value });
    };
    
    const handlePriceMaxChange = (value) => {
      setLocalPriceMax(value);
      updateFiltersDebounced({ ...filters, price_max: value });
    };
    
    // Count local sensitive filters
    const localSensitiveCount = () => {
      return localSensitiveTopics.length;
    };
    
    // Toggle sensitive topic and apply filter immediately
    const toggleSensitiveTopic = (topicKey) => {
      const newTopics = localSensitiveTopics.includes(topicKey)
        ? localSensitiveTopics.filter(k => k !== topicKey)
        : [...localSensitiveTopics, topicKey];
      
      setLocalSensitiveTopics(newTopics);
      
      const newFilters = {
        ...filters,
        search: localSearch,
        dr_min: localDrMin,
        dr_max: localDrMax,
        traffic_min: localTrafficMin,
        traffic_max: localTrafficMax,
        sensitiveTopics: newTopics,
      };
      setFilters(newFilters);
      fetchProducts(1, newFilters);
    };
    
    // Clear all sensitive topics
    const clearAllSensitiveTopics = () => {
      setLocalSensitiveTopics([]);
      const newFilters = {
        ...filters,
        sensitiveTopics: [],
      };
      setFilters(newFilters);
      fetchProducts(1, newFilters);
    };
    
    // Sync all local state to parent and apply filters
    const handleApplyWithSync = () => {
      // Create new filters object with local values
      const newFilters = {
        ...filters,
        search: localSearch,
        dr_min: localDrMin,
        dr_max: localDrMax,
        traffic_min: localTrafficMin,
        traffic_max: localTrafficMax,
        price_min: localPriceMin,
        price_max: localPriceMax,
        sensitiveTopics: localSensitiveTopics,
      };
      
      // Update state
      setFilters(newFilters);
      
      // Fetch with new filters immediately (don't wait for state update)
      fetchProducts(1, newFilters);
      setMobileFiltersOpen(false);
    };
    
    // Apply country filter immediately
    const handleCountrySelect = (country) => {
      const newCountries = country === 'all' 
        ? []
        : filters.countries.includes(country)
          ? filters.countries.filter(c => c !== country)
          : [...filters.countries, country];
      const newFilters = {
        ...filters,
        countries: newCountries,
        search: localSearch,
        dr_min: localDrMin,
        dr_max: localDrMax,
        traffic_min: localTrafficMin,
        traffic_max: localTrafficMax,
        sensitiveTopics: localSensitiveTopics,
      };
      setFilters(newFilters);
      fetchProducts(1, newFilters);
    };
    
    // Apply category filter immediately
    const handleCategorySelect = (category) => {
      const newCategories = category === 'all' 
        ? []
        : filters.categories.includes(category)
          ? filters.categories.filter(c => c !== category)
          : [...filters.categories, category];
      const newFilters = {
        ...filters,
        categories: newCategories,
        search: localSearch,
        dr_min: localDrMin,
        dr_max: localDrMax,
        traffic_min: localTrafficMin,
        traffic_max: localTrafficMax,
        sensitiveTopics: localSensitiveTopics,
      };
      setFilters(newFilters);
      fetchProducts(1, newFilters);
    };
    
    return (
    <div className="p-4 space-y-2">
      {/* Action Buttons - At Top */}
      <div className="flex flex-col gap-2 pb-4 border-b border-border mb-4">
        <Button onClick={handleApplyWithSync} className="w-full">
          <Search className="h-4 w-4 mr-2" />
          {t.applyFilters}
        </Button>
        <Button variant="destructive" onClick={() => {
          // Reset all local states
          setLocalSearch('');
          setLocalDrMin('');
          setLocalDrMax('');
          setLocalTrafficMin('');
          setLocalTrafficMax('');
          setLocalPriceMin('');
          setLocalPriceMax('');
          setLocalSensitiveTopics([]);
          handleClearFilters();
        }} className="w-full">
          <RotateCcw className="h-4 w-4 mr-2" />
          {t.resetFilters}
        </Button>
      </div>

      <Accordion 
        type="multiple" 
        value={expandedSections} 
        onValueChange={setExpandedSections}
        className="space-y-2"
      >
        {/* Search Filter */}
        <AccordionItem value="search" className="border border-border rounded-lg px-4">
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-primary" />
              <span className="font-medium">{t.searchLabel}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <Input
              type="text"
              placeholder={t.search}
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Country Filter */}
        <AccordionItem value="country" className="border border-border rounded-lg px-4">
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              <span className="font-medium">{t.country}</span>
              {filters.countries && filters.countries.length > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">{filters.countries.length}</Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-2">
            <Input
              type="text"
              placeholder={t.searchCountry}
              value={localCountrySearch}
              onChange={(e) => setLocalCountrySearch(e.target.value)}
              className="mb-2"
            />
            <div className="max-h-[200px] overflow-y-auto border rounded-md">
              <div 
                className={`px-3 py-2 cursor-pointer hover:bg-muted ${!filters.countries || filters.countries.length === 0 ? 'bg-primary/10 font-medium' : ''}`}
                onClick={() => handleCountrySelect('all')}
              >
                {t.allCountries}
              </div>
              {uniqueCountries
                .filter(country => {
                  const translated = translateCountry(country, language);
                  return country.toLowerCase().includes(localCountrySearch.toLowerCase()) ||
                         translated.toLowerCase().includes(localCountrySearch.toLowerCase());
                })
                .map((country) => (
                  <div 
                    key={country}
                    className={`px-3 py-2 cursor-pointer hover:bg-muted flex items-center gap-2 ${filters.countries && filters.countries.includes(country) ? 'bg-primary/10 font-medium' : ''}`}
                    onClick={() => handleCountrySelect(country)}
                  >
                    <Checkbox checked={filters.countries && filters.countries.includes(country)} className="pointer-events-none" />
                    {translateCountry(country, language)}
                  </div>
                ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {uniqueCountries.filter(c => {
                const translated = translateCountry(c, language);
                return c.toLowerCase().includes(localCountrySearch.toLowerCase()) ||
                       translated.toLowerCase().includes(localCountrySearch.toLowerCase());
              }).length} {t.countries}
            </p>
          </AccordionContent>
        </AccordionItem>

        {/* Category Filter */}
        <AccordionItem value="category" className="border border-border rounded-lg px-4">
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-primary" />
              <span className="font-medium">{t.category}</span>
              {filters.categories && filters.categories.length > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">{filters.categories.length}</Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-2">
            <Input
              type="text"
              placeholder={t.searchCategory}
              value={localCategorySearch}
              onChange={(e) => setLocalCategorySearch(e.target.value)}
              className="mb-2"
            />
            <div className="max-h-[200px] overflow-y-auto border rounded-md">
              <div 
                className={`px-3 py-2 cursor-pointer hover:bg-muted ${!filters.categories || filters.categories.length === 0 ? 'bg-primary/10 font-medium' : ''}`}
                onClick={() => handleCategorySelect('all')}
              >
                {t.allCategories}
              </div>
              {uniqueCategories
                .filter(cat => {
                  const translated = translateCategory(cat, language);
                  return cat.toLowerCase().includes(localCategorySearch.toLowerCase()) ||
                         translated.toLowerCase().includes(localCategorySearch.toLowerCase());
                })
                .map((cat) => (
                  <div 
                    key={cat}
                    className={`px-3 py-2 cursor-pointer hover:bg-muted flex items-center gap-2 ${filters.categories && filters.categories.includes(cat) ? 'bg-primary/10 font-medium' : ''}`}
                    onClick={() => handleCategorySelect(cat)}
                  >
                    <Checkbox checked={filters.categories && filters.categories.includes(cat)} className="pointer-events-none" />
                    {translateCategory(cat, language)}
                  </div>
                ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {uniqueCategories.filter(cat => {
                const translated = translateCategory(cat, language);
                return cat.toLowerCase().includes(localCategorySearch.toLowerCase()) ||
                       translated.toLowerCase().includes(localCategorySearch.toLowerCase());
              }).length} {t.categories}
            </p>
          </AccordionContent>
        </AccordionItem>

        {/* DR Filter */}
        <AccordionItem value="dr" className="border border-border rounded-lg px-4">
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="h-4 w-4 text-primary font-bold text-xs">DR</span>
              <span className="font-medium">{t.dr}</span>
              {(localDrMin || localDrMax) && (
                <Badge variant="secondary" className="ml-2 text-xs">1</Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="dr-min" className="text-xs text-muted-foreground">{t.drMin}</Label>
                <Input
                  id="dr-min"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  value={localDrMin}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val !== '' && Number(val) > 100) val = '100';
                    if (val !== '' && Number(val) < 0) val = '0';
                    handleDrMinChange(val);
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="dr-max" className="text-xs text-muted-foreground">{t.drMax}</Label>
                <Input
                  id="dr-max"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="100"
                  value={localDrMax}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val !== '' && Number(val) > 100) val = '100';
                    if (val !== '' && Number(val) < 0) val = '0';
                    handleDrMaxChange(val);
                  }}
                  className="mt-1"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {language === 'es' ? 'Rango de 0 a 100' : 'Range from 0 to 100'}
            </p>
          </AccordionContent>
        </AccordionItem>

        {/* Organic Traffic Filter */}
        <AccordionItem value="traffic" className="border border-border rounded-lg px-4">
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="font-medium">{t.traffic}</span>
              {(localTrafficMin || localTrafficMax) && (
                <Badge variant="secondary" className="ml-2 text-xs">1</Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="traffic-min" className="text-xs text-muted-foreground">{t.trafficMin}</Label>
                <Input
                  id="traffic-min"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={localTrafficMin}
                  onChange={(e) => handleTrafficMinChange(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="traffic-max" className="text-xs text-muted-foreground">{t.trafficMax}</Label>
                <Input
                  id="traffic-max"
                  type="number"
                  min="0"
                  placeholder="∞"
                  value={localTrafficMax}
                  onChange={(e) => handleTrafficMaxChange(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {language === 'es' ? 'Visitas mensuales estimadas' : 'Estimated monthly visits'}
            </p>
          </AccordionContent>
        </AccordionItem>

        {/* Price Filter */}
        <AccordionItem value="price" className="border border-border rounded-lg px-4">
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="h-4 w-4 text-primary font-bold text-xs">€</span>
              <span className="font-medium">{t.priceRange}</span>
              {(localPriceMin || localPriceMax) && (
                <Badge variant="secondary" className="ml-2 text-xs">1</Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="price-min" className="text-xs text-muted-foreground">{t.minPrice}</Label>
                <Input
                  id="price-min"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={localPriceMin}
                  onChange={(e) => handlePriceMinChange(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="price-max" className="text-xs text-muted-foreground">{t.maxPrice}</Label>
                <Input
                  id="price-max"
                  type="number"
                  min="0"
                  placeholder="∞"
                  value={localPriceMax}
                  onChange={(e) => handlePriceMaxChange(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {language === 'es' ? 'Precio en euros (€)' : 'Price in euros (€)'}
            </p>
          </AccordionContent>
        </AccordionItem>

        {/* Sensitive Topics Filter - Dynamic from backend */}
        <AccordionItem value="sensitive" className="border border-border rounded-lg px-4">
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              <span className="font-medium">{t.sensitiveTopics}</span>
              {localSensitiveCount() > 0 && (
                <Badge className="ml-2 text-xs bg-orange-500 text-white">
                  {localSensitiveCount()}
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 space-y-2">
            <p className="text-xs text-muted-foreground mb-3">
              {language === 'es' 
                ? 'Selecciona las temáticas que debe aceptar el medio' 
                : 'Select topics the site must accept'}
            </p>
            
            {/* Search for sensitive topics */}
            <Input
              type="text"
              placeholder={language === 'es' ? 'Buscar temática...' : 'Search topic...'}
              value={localSensitiveSearch}
              onChange={(e) => setLocalSensitiveSearch(e.target.value)}
              className="mb-2"
            />
            
            <div className="max-h-[200px] overflow-y-auto border rounded-md">
              {sensitiveTopicOptions
                .filter(topic => 
                  !localSensitiveSearch || 
                  topic.label.toLowerCase().includes(localSensitiveSearch.toLowerCase()) ||
                  topic.key.toLowerCase().includes(localSensitiveSearch.toLowerCase())
                )
                .map((topic) => (
                <div 
                  key={topic.key}
                  className={`px-3 py-2 cursor-pointer hover:bg-muted flex items-center gap-2 ${localSensitiveTopics.includes(topic.key) ? 'bg-orange-100 dark:bg-orange-900/30' : ''}`}
                  onClick={() => toggleSensitiveTopic(topic.key)}
                >
                  <Checkbox checked={localSensitiveTopics.includes(topic.key)} className="pointer-events-none" />
                  <span className={localSensitiveTopics.includes(topic.key) ? 'font-medium text-orange-700 dark:text-orange-300' : ''}>{topic.label}</span>
                </div>
              ))}
              {sensitiveTopicOptions.length === 0 && (
                <div className="px-3 py-4 text-center text-muted-foreground text-sm">
                  {language === 'es' ? 'No hay temáticas sensibles configuradas' : 'No sensitive topics configured'}
                </div>
              )}
            </div>
            
            {localSensitiveCount() > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {localSensitiveTopics.map(topicKey => {
                  const topic = sensitiveTopicOptions.find(t => t.key === topicKey);
                  return topic ? (
                    <Badge key={topicKey} className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 px-2 py-1 flex items-center gap-1">
                      {topic.label}
                      <button onClick={() => toggleSensitiveTopic(topicKey)} className="ml-1 hover:bg-orange-200 dark:hover:bg-orange-800 rounded-full p-0.5">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ) : null;
                })}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearAllSensitiveTopics}
                  className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  {language === 'es' ? 'Limpiar todo' : 'Clear all'}
                </Button>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <HeroSection productCount={pagination.total} />

        {/* Catalog Section */}
        <section id="catalog" className="py-8 lg:py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Header with Title and Download */}
            <div className="flex flex-col items-center text-center gap-4 mb-6">
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
                  {t.catalog}
                </h2>
                <p className="text-muted-foreground mt-1">
                  {pagination.total.toLocaleString()} {t.results}
                </p>
              </div>
              <Button
                onClick={() => setShowDownloadConfirm(true)}
                disabled={downloading || pagination.total === 0}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg"
              >
                {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                {language === 'es' ? 'Descarga de Medios' : 'Download Media'}
              </Button>
            </div>

            {/* Download Confirmation Dialog */}
            {showDownloadConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4">
                  <div className="text-center">
                    <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                      <Download className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {language === 'es' ? 'Confirmar Descarga' : 'Confirm Download'}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {language === 'es' ? 'Se descargarán' : 'Will download'} <span className="font-bold text-emerald-600">{pagination.total.toLocaleString()}</span> {language === 'es' ? 'medios' : 'media'}
                    </p>
                    <div className="flex gap-3 justify-center">
                      <Button variant="outline" onClick={() => setShowDownloadConfirm(false)}>
                        {language === 'es' ? 'Cancelar' : 'Cancel'}
                      </Button>
                      <Button onClick={handleDownloadFiltered} className="bg-emerald-600 hover:bg-emerald-700">
                        <Download className="h-4 w-4 mr-2" />
                        {language === 'es' ? 'Descargar' : 'Download'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* HORIZONTAL FILTERS BAR - Like purolink.store */}
            <div className="bg-card border border-border rounded-xl p-6 mb-6 shadow-sm">
              {/* Header */}
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
                <Filter className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-lg">{t.filters}</h3>
                {activeFiltersCount() > 0 && (
                  <Badge variant="secondary" className="ml-2">{activeFiltersCount()}</Badge>
                )}
              </div>

              {/* First Row - Search, Country, Category, Search Button */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                    {language === 'es' ? 'Buscar' : 'Search'}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t.search}
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>

                {/* Country */}
                <div data-dropdown="countries">
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                    {t.country}
                  </label>
                  <div className="relative">
                    <button
                      onClick={() => { setCountriesOpen(!countriesOpen); setCategoriesOpen(false); }}
                      className="w-full flex items-center justify-between px-3 py-2 h-10 border border-input rounded-md bg-background hover:bg-emerald-50 hover:border-emerald-300 text-sm"
                    >
                      <span className="truncate">
                        {filters.countries.length === 0 
                          ? t.allCountries 
                          : filters.countries.length === 1 
                            ? translateCountry(filters.countries[0], language)
                            : `${filters.countries.length} ${language === 'es' ? 'países' : 'countries'}`
                        }
                      </span>
                      <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${countriesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {countriesOpen && (
                      <div className="absolute z-50 mt-1 w-full max-h-72 overflow-hidden bg-white border border-border rounded-md shadow-lg">
                        <div className="p-2 border-b sticky top-0 bg-white">
                          <Input
                            placeholder={t.searchCountry}
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            className="h-8 text-sm"
                            autoFocus
                          />
                        </div>
                        <div className="p-2 max-h-52 overflow-auto">
                          {filters.countries.length > 0 && (
                            <button
                              onClick={() => { setFilters(prev => ({ ...prev, countries: [] })); setTimeout(() => fetchProducts(1, { ...filters, countries: [] }), 0); }}
                              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded flex items-center gap-2 border-b mb-2"
                            >
                              <RotateCcw className="h-3 w-3" />
                              {language === 'es' ? 'Limpiar selección' : 'Clear selection'}
                            </button>
                          )}
                          {!countrySearch && (
                            <button
                              onClick={() => { setFilters(prev => ({ ...prev, countries: [] })); setTimeout(() => fetchProducts(1, { ...filters, countries: [] }), 0); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                            >
                              <Checkbox checked={filters.countries.length === 0} className="pointer-events-none" />
                              {t.allCountries}
                            </button>
                          )}
                          {uniqueCountries
                            .filter(country => 
                              !countrySearch || 
                              country.toLowerCase().includes(countrySearch.toLowerCase()) ||
                              translateCountry(country, language).toLowerCase().includes(countrySearch.toLowerCase())
                            )
                            .map(country => (
                            <button
                              key={country}
                              onClick={() => toggleCountry(country)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                            >
                              <Checkbox checked={filters.countries.includes(country)} className="pointer-events-none" />
                              {translateCountry(country, language)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Category */}
                <div data-dropdown="categories">
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                    {t.category}
                  </label>
                  <div className="relative">
                    <button
                      onClick={() => { setCategoriesOpen(!categoriesOpen); setCountriesOpen(false); }}
                      className="w-full flex items-center justify-between px-3 py-2 h-10 border border-input rounded-md bg-background hover:bg-emerald-50 hover:border-emerald-300 text-sm"
                    >
                      <span className="truncate">
                        {filters.categories.length === 0 
                          ? t.allCategories 
                          : filters.categories.length === 1 
                            ? translateCategory(filters.categories[0], language)
                            : `${filters.categories.length} ${language === 'es' ? 'categorías' : 'categories'}`
                        }
                      </span>
                      <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${categoriesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {categoriesOpen && (
                      <div className="absolute z-50 mt-1 w-full max-h-72 overflow-hidden bg-white border border-border rounded-md shadow-lg">
                        <div className="p-2 border-b sticky top-0 bg-white">
                          <Input
                            placeholder={t.searchCategory}
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            className="h-8 text-sm"
                            autoFocus
                          />
                        </div>
                        <div className="p-2 max-h-52 overflow-auto">
                          {filters.categories.length > 0 && (
                            <button
                              onClick={() => { setFilters(prev => ({ ...prev, categories: [] })); setTimeout(() => fetchProducts(1, { ...filters, categories: [] }), 0); }}
                              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded flex items-center gap-2 border-b mb-2"
                            >
                              <RotateCcw className="h-3 w-3" />
                              {language === 'es' ? 'Limpiar selección' : 'Clear selection'}
                            </button>
                          )}
                          {!categorySearch && (
                            <button
                              onClick={() => { setFilters(prev => ({ ...prev, categories: [] })); setTimeout(() => fetchProducts(1, { ...filters, categories: [] }), 0); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                            >
                              <Checkbox checked={filters.categories.length === 0} className="pointer-events-none" />
                              {t.allCategories}
                            </button>
                          )}
                          {uniqueCategories
                            .filter(category => 
                              !categorySearch || 
                              category.toLowerCase().includes(categorySearch.toLowerCase()) ||
                              translateCategory(category, language).toLowerCase().includes(categorySearch.toLowerCase())
                            )
                            .map(category => (
                            <button
                              key={category}
                              onClick={() => toggleCategory(category)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                            >
                              <Checkbox checked={filters.categories.includes(category)} className="pointer-events-none" />
                              {translateCategory(category, language)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Site Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">{language === 'es' ? 'Tipo Medio' : 'Site Type'}</label>
                  <div className="relative" data-dropdown="siteTypes">
                    <Button
                      variant="outline"
                      onClick={() => setSiteTypesOpen(!siteTypesOpen)}
                      className="w-full justify-between h-10"
                    >
                      <span className="truncate">
                        {filters.siteTypes.length === 0 
                          ? (language === 'es' ? 'Todos' : 'All')
                          : filters.siteTypes.length === 1 
                            ? filters.siteTypes[0]
                            : `${filters.siteTypes.length} ${language === 'es' ? 'seleccionados' : 'selected'}`}
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                    {siteTypesOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-white rounded-md border shadow-lg max-h-60 overflow-auto">
                        <div className="p-2">
                          {filters.siteTypes.length > 0 && (
                            <button
                              onClick={() => { setFilters(prev => ({ ...prev, siteTypes: [] })); setTimeout(() => fetchProducts(1, { ...filters, siteTypes: [] }), 0); }}
                              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded flex items-center gap-2 border-b mb-2"
                            >
                              <RotateCcw className="h-3 w-3" />
                              {language === 'es' ? 'Limpiar selección' : 'Clear selection'}
                            </button>
                          )}
                          <button
                            onClick={() => { setFilters(prev => ({ ...prev, siteTypes: [] })); setTimeout(() => fetchProducts(1, { ...filters, siteTypes: [] }), 0); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                          >
                            <Checkbox checked={filters.siteTypes.length === 0} className="pointer-events-none" />
                            {language === 'es' ? 'Todos' : 'All'}
                          </button>
                          {siteTypeOptions.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => {
                                const newTypes = filters.siteTypes.includes(opt.value)
                                  ? filters.siteTypes.filter(t => t !== opt.value)
                                  : [...filters.siteTypes, opt.value];
                                setFilters(prev => ({ ...prev, siteTypes: newTypes }));
                                setTimeout(() => fetchProducts(1, { ...filters, siteTypes: newTypes }), 0);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 rounded flex items-center gap-2"
                            >
                              <Checkbox checked={filters.siteTypes.includes(opt.value)} className="pointer-events-none" />
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Search Button */}
                <div className="flex items-end gap-2">
                  <Button onClick={handleApplyFilters} className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700">
                    <Search className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Buscar' : 'Search'}
                  </Button>
                  <Button variant="destructive" onClick={handleClearFilters} className="bg-red-600 hover:bg-red-700">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {t.clearFilters}
                  </Button>
                </div>
              </div>

              {/* Second Row - DR, Traffic, Price */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
                {/* DR Min */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">DR Min</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={filters.dr_min}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters(prev => ({ ...prev, dr_min: value }));
                      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                      filterDebounceRef.current = setTimeout(() => fetchProducts(1, { ...filters, dr_min: value }), 500);
                    }}
                    min="0"
                    max="100"
                  />
                </div>
                {/* DR Max */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">DR Max</label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={filters.dr_max}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters(prev => ({ ...prev, dr_max: value }));
                      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                      filterDebounceRef.current = setTimeout(() => fetchProducts(1, { ...filters, dr_max: value }), 500);
                    }}
                    min="0"
                    max="100"
                  />
                </div>
                {/* Traffic Min */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                    {language === 'es' ? 'Tráfico Min' : 'Traffic Min'}
                  </label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={filters.traffic_min}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters(prev => ({ ...prev, traffic_min: value }));
                      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                      filterDebounceRef.current = setTimeout(() => fetchProducts(1, { ...filters, traffic_min: value }), 500);
                    }}
                  />
                </div>
                {/* Traffic Max */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                    {language === 'es' ? 'Tráfico Max' : 'Traffic Max'}
                  </label>
                  <Input
                    type="number"
                    placeholder="∞"
                    value={filters.traffic_max}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters(prev => ({ ...prev, traffic_max: value }));
                      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                      filterDebounceRef.current = setTimeout(() => fetchProducts(1, { ...filters, traffic_max: value }), 500);
                    }}
                  />
                </div>
                {/* Price Min */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                    {language === 'es' ? 'Precio Min €' : 'Price Min €'}
                  </label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={filters.price_min || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters(prev => ({ ...prev, price_min: value }));
                      // Trigger debounced search
                      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                      filterDebounceRef.current = setTimeout(() => fetchProducts(1, { ...filters, price_min: value }), 500);
                    }}
                  />
                </div>
                {/* Price Max */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                    {language === 'es' ? 'Precio Max €' : 'Price Max €'}
                  </label>
                  <Input
                    type="number"
                    placeholder="∞"
                    value={filters.price_max || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilters(prev => ({ ...prev, price_max: value }));
                      // Trigger debounced search
                      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                      filterDebounceRef.current = setTimeout(() => fetchProducts(1, { ...filters, price_max: value }), 500);
                    }}
                  />
                </div>
              </div>

              {/* Sensitive Topics Dropdown - Same style as Categories with search */}
              <div className="border-t border-border pt-4">
                <div className="relative" data-dropdown="sensitiveTopics">
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                    {t.sensitiveTopics}
                  </label>
                  <button
                    className={`w-full flex items-center justify-between px-3 py-2 h-10 border rounded-md bg-background hover:bg-orange-50 hover:border-orange-300 text-sm ${
                      filters.sensitiveTopics.length > 0 ? 'border-orange-400 bg-orange-50' : 'border-input'
                    }`}
                    onClick={() => { setSensitiveTopicsOpen(!sensitiveTopicsOpen); setCountriesOpen(false); setCategoriesOpen(false); setSiteTypesOpen(false); }}
                  >
                    <span className="truncate">
                      {filters.sensitiveTopics.length === 0 
                        ? (language === 'es' ? 'Ninguna' : 'None')
                        : filters.sensitiveTopics.length === 1 
                          ? sensitiveTopicOptions.find(t => t.key === filters.sensitiveTopics[0])?.label || filters.sensitiveTopics[0]
                          : `${filters.sensitiveTopics.length} ${language === 'es' ? 'seleccionadas' : 'selected'}`
                      }
                    </span>
                    {filters.sensitiveTopics.length > 0 && (
                      <Badge className="ml-2 bg-orange-500 text-white text-xs">
                        {filters.sensitiveTopics.length}
                      </Badge>
                    )}
                    <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${sensitiveTopicsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {sensitiveTopicsOpen && (
                    <div className="absolute z-50 mt-1 w-full max-h-72 overflow-hidden bg-white border border-border rounded-md shadow-lg">
                      <div className="p-2 border-b sticky top-0 bg-white">
                        <Input
                          placeholder={language === 'es' ? 'Buscar temática...' : 'Search topic...'}
                          value={sensitiveTopicSearch}
                          onChange={(e) => setSensitiveTopicSearch(e.target.value)}
                          className="h-8 text-sm"
                          autoFocus
                        />
                      </div>
                      <div className="p-2 max-h-52 overflow-auto">
                        {filters.sensitiveTopics.length > 0 && (
                          <button
                            onClick={() => { 
                              const newFilters = { ...filters, sensitiveTopics: [] };
                              setFilters(newFilters); 
                              setTimeout(() => fetchProducts(1, newFilters), 0); 
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded flex items-center gap-2 border-b mb-2"
                          >
                            <RotateCcw className="h-3 w-3" />
                            {language === 'es' ? 'Limpiar selección' : 'Clear selection'}
                          </button>
                        )}
                        {sensitiveTopicOptions
                          .filter(topic => 
                            !sensitiveTopicSearch || 
                            topic.label.toLowerCase().includes(sensitiveTopicSearch.toLowerCase()) ||
                            topic.key.toLowerCase().includes(sensitiveTopicSearch.toLowerCase())
                          )
                          .map(topic => (
                          <button
                            key={topic.key}
                            onClick={() => {
                              const isSelected = filters.sensitiveTopics.includes(topic.key);
                              const newTopics = isSelected
                                ? filters.sensitiveTopics.filter(t => t !== topic.key)
                                : [...filters.sensitiveTopics, topic.key];
                              const newFilters = { ...filters, sensitiveTopics: newTopics };
                              setFilters(newFilters);
                              setTimeout(() => fetchProducts(1, newFilters), 0);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm rounded flex items-center gap-2 ${filters.sensitiveTopics.includes(topic.key) ? 'bg-orange-100' : 'hover:bg-orange-50'}`}
                          >
                            <Checkbox checked={filters.sensitiveTopics.includes(topic.key)} className="pointer-events-none" />
                            <span className={filters.sensitiveTopics.includes(topic.key) ? 'font-medium text-orange-700' : ''}>{topic.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Active filters display */}
              {hasActiveFilters() && (
                <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
                  {filters.search && (
                    <Badge variant="secondary" className="gap-1">
                      {language === 'es' ? 'Búsqueda' : 'Search'}: {filters.search}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange('search', '')} />
                    </Badge>
                  )}
                  {filters.countries.map(c => (
                    <Badge key={c} variant="secondary" className="gap-1 bg-emerald-100 text-emerald-800">
                      {translateCountry(c, language)}
                      <X className="h-3 w-3 cursor-pointer hover:bg-emerald-200 rounded-full" onClick={() => toggleCountry(c)} />
                    </Badge>
                  ))}
                  {filters.categories.map(c => (
                    <Badge key={c} variant="secondary" className="gap-1 bg-blue-100 text-blue-800">
                      {translateCategory(c, language)}
                      <X className="h-3 w-3 cursor-pointer hover:bg-blue-200 rounded-full" onClick={() => toggleCategory(c)} />
                    </Badge>
                  ))}
                  {filters.sensitiveTopics.map(topicKey => {
                    const topic = sensitiveTopicOptions.find(t => t.key === topicKey);
                    return topic ? (
                      <Badge key={topicKey} className="gap-1 bg-orange-100 text-orange-800">
                        {topic.label}
                        <X className="h-3 w-3 cursor-pointer hover:bg-orange-200 rounded-full" onClick={() => {
                          const newTopics = filters.sensitiveTopics.filter(t => t !== topicKey);
                          const newFilters = { ...filters, sensitiveTopics: newTopics };
                          setFilters(newFilters);
                          fetchProducts(1, newFilters);
                        }} />
                      </Badge>
                    ) : null;
                  })}
                  {(filters.dr_min || filters.dr_max) && (
                    <Badge variant="secondary" className="gap-1">
                      DR: {filters.dr_min || '0'} - {filters.dr_max || '100'}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => { handleFilterChange('dr_min', ''); handleFilterChange('dr_max', ''); }} />
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* View Controls */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {pagination.total.toLocaleString()} {language === 'es' ? 'productos' : 'products'}
                </span>
                <span className="text-sm text-muted-foreground">|</span>
                <span className="text-sm text-muted-foreground">{t.showingItems}</span>
                <select 
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="border border-border rounded-md px-2 py-1 text-sm bg-background"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                </select>
                <span className="text-sm text-muted-foreground">{t.itemsPerPage}</span>
              </div>
              
              <div className="flex items-center border border-border rounded-lg p-1">
                <Button
                  variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className="h-8 w-8 p-0"
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="h-8 w-8 p-0"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Products Display */}
            {loading && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
            
            {!loading && products.length > 0 && (
              <>
                {viewMode === 'table' ? (
                  <ProductTable 
                    products={products} 
                    texts={t} 
                    onProductClick={openProductDialog}
                    onSortChange={handleSortChange}
                    currentSortField={sortField}
                    currentSortOrder={sortOrder}
                  />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {products.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Pagination */}
            {!loading && products.length > 0 && (
              <PaginationControls 
                pagination={pagination} 
                onPageChange={handlePageChange} 
              />
            )}

            {/* No Results */}
            {!loading && products.length === 0 && (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-muted mb-4">
                  <Filter className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{t.noResults}</h3>
                <p className="text-muted-foreground mb-4">{t.tryOther}</p>
                <Button variant="outline" onClick={handleClearFilters}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t.clearFilters}
                </Button>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Product Detail Dialog */}
      <ProductDetailDialog
        product={selectedProduct}
        open={productDialogOpen}
        onOpenChange={setProductDialogOpen}
        onFindSimilar={findSimilarSites}
        language={language}
        texts={t}
        sensitiveCategories={sensitiveCategories}
      />

      <Footer />
    </div>
  );
};

export default HomePage;
